import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SearchX, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { slotStatsApi } from '../lib/api';
import { classifyR14Error, r14ErrorText } from '../lib/r14Errors';
import { r14bUiEnabled } from '../lib/r14Switch';

/*
 * Round 14: "targets not found (skipped)" for one slot, with Restore.
 *
 * The worker counts a target as missed when a round-13-or-later search does not find it; after 3
 * misses on 3 different days the slot stops using it and reports it. The server keeps that list;
 * this card shows it (on Slot Stats and on the Target Data page) and puts a target back into use.
 * When the slot's "Auto-remove not-found targets from the list" setting (Target Data page) is ON,
 * a skipped target was also taken out of the target list - Restore puts it back there too (the
 * server does both; this card only asks).
 *
 * Route missing (404/405 without one of the server's codes) = a server without round 14. The card
 * says that, and never shows an empty list for it: "nothing skipped" and "cannot tell" are
 * different answers. A 404 WITH a code (not_enabled, slot_not_found) says what the server said
 * (lib/r14Errors.js, note N1).
 *
 * Round 14b (W11, contract C2; switch r14bUiEnabled in lib/r14Switch.js): misses are counted per
 * list type + target, so the same target can be skipped as a hashtag and still be used as a
 * competitor account. Each row shows its type (typeLabel below), rows are keyed by target + type,
 * and Restore sends {target, type} - {target} alone for a row without a type (the server then
 * restores every type of it). Switch OFF = the round-14 card exactly.
 */

/*
 * The worker's target-type constants - the same values as the Target Data page's target types
 * (pages/SlotTargetDataPage.js TARGET_TYPES) - as a label for ONE target. A display map, not an
 * option list: nothing is picked from it. A type not in it gets no label (it is still sent back
 * on Restore exactly as the server gave it).
 */
const TYPE_LABELS = {
  COMPETITORACCOUNTS: 'Competitor account',
  HASHTAGS: 'Hashtag',
  LOCATIONS: 'Location',
  DIRECTACCOUNTS: 'Direct account',
};

/** A row's type as a readable label; null for a missing or unknown type. Pure - exported for tests. */
export function typeLabel(type) {
  if (type === null || type === undefined) return null;
  return TYPE_LABELS[String(type).trim().toUpperCase()] || null;
}

function cleanType(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

/**
 * skippedSince is the server's zone-less LocalDateTime in the slot's time zone
 * ("2026-09-28T10:00:00.123"). Shown as "2026-09-28 10:00 (slot time)"; a bare date as it is;
 * anything else raw (note N2).
 */
export function formatSkippedSince(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === '') return null;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (m) return `${m[1]} ${m[2]} (slot time)`;
  return s;
}

/** Accepts the documented shape and the obvious variants of it. Pure - exported for tests. */
export function normaliseNotFound(data) {
  const list = Array.isArray(data) ? data : (data && (data.targets || data.items)) || [];
  const rows = list
    .map((t) => {
      if (typeof t === 'string') return { target: t, misses: null, missDays: [], skippedSince: null, removedFromList: false, type: null };
      if (!t) return null;
      const target = t.target ?? t.username ?? t.name;
      if (!target) return null;
      let days = t.missDays ?? t.days ?? [];
      if (typeof days === 'string') days = days.split(',').map((d) => d.trim()).filter(Boolean);
      return {
        target: String(target),
        misses: t.misses ?? t.missCount ?? (Array.isArray(days) ? days.length : null),
        missDays: Array.isArray(days) ? days : [],
        skippedSince: t.skippedSince ?? t.skippedAt ?? t.reportedAt ?? null,
        removedFromList: t.removedFromList === true,
        type: cleanType(t.type ?? t.targetType ?? t.target_type),
      };
    })
    .filter(Boolean);
  const autoRemove = data && !Array.isArray(data) && typeof data.autoRemove === 'boolean' ? data.autoRemove : null;
  return { rows, autoRemove };
}

export default function NotFoundTargetsCard({ slotName }) {
  const queryClient = useQueryClient();
  // Round 14b: OFF = the round-14 card (no type label, rows keyed by target, restore {target}).
  const typed = r14bUiEnabled();
  const q = useQuery({
    queryKey: ['notFoundTargets', slotName],
    queryFn: () => slotStatsApi.notFoundTargets(slotName).then((r) => r.data),
    retry: false,
  });

  // The row's type as this card uses it: null with the switch OFF or when the server sent none.
  const typeOf = (r) => (typed ? r.type : null);
  const rowKey = (r) => (typeOf(r) ? `${r.target}|${r.type}` : r.target);
  // For logs: the raw type; for people: the label (an unknown type is not shown to them).
  const logName = (r) => (typeOf(r) ? `${r.target} [type ${r.type}]` : r.target);
  const shownName = (r) => {
    const label = typeOf(r) ? typeLabel(r.type) : null;
    return label ? `${r.target} (${label})` : r.target;
  };

  const restore = useMutation({
    mutationFn: (row) => {
      const type = typeOf(row);
      if (typed) {
        // eslint-disable-next-line no-console
        console.info(`[R14-UI] not-found targets: restoring ${logName(row)} on ${slotName}`
          + (type ? ' (body {target, type})' : ' (no type on the row: body {target} = every type of it)'));
      }
      return type
        ? slotStatsApi.restoreNotFound(slotName, row.target, type)
        : slotStatsApi.restoreNotFound(slotName, row.target);
    },
    onSuccess: (res, row) => {
      // eslint-disable-next-line no-console
      console.info(`[R14-UI] not-found targets: restored ${logName(row)} on ${slotName}`, res && res.data);
      toast.success(`${shownName(row)} restored - the slot will use it again`);
      queryClient.invalidateQueries({ queryKey: ['notFoundTargets', slotName] });
    },
    onError: (err, row) => {
      const c = classifyR14Error(err);
      // eslint-disable-next-line no-console
      console.warn(`[R14-UI] not-found targets: restore of ${logName(row)} on ${slotName} failed`, c.status, c.code || '', c.message || '');
      toast.error(r14ErrorText(err, `Could not restore ${shownName(row)}`));
      // The list on screen is stale (restored in another tab, or the slot is gone): re-read it.
      if (c.kind === 'targetNotListed' || c.kind === 'slotNotFound') {
        queryClient.invalidateQueries({ queryKey: ['notFoundTargets', slotName] });
      }
    },
  });

  let body;
  const qErr = q.error ? classifyR14Error(q.error) : null;
  if (q.isLoading) {
    body = <p className="text-sm text-muted-foreground">Loading...</p>;
  } else if (qErr && qErr.kind === 'routeMissing') {
    body = (
      <p className="text-sm text-muted-foreground" data-testid="not-found-unavailable">
        This server does not report skipped targets yet (it needs the round-14 server).
      </p>
    );
  } else if (qErr && qErr.kind === 'notEnabled') {
    body = (
      <p className="text-sm text-muted-foreground" data-testid="not-found-disabled">
        {qErr.message || 'The not-found targets list is switched off on this server.'}
      </p>
    );
  } else if (qErr && qErr.kind === 'slotNotFound') {
    body = (
      <p className="text-sm text-destructive" data-testid="not-found-no-slot">
        {qErr.message || `No slot ${slotName} on this account.`}
      </p>
    );
  } else if (q.error) {
    body = (
      <p className="text-sm text-destructive" data-testid="not-found-error">
        Could not load the skipped targets ({r14ErrorText(q.error, 'no answer')}).
      </p>
    );
  } else {
    const { rows, autoRemove } = normaliseNotFound(q.data);
    body = (
      <div className="space-y-3">
        {autoRemove !== null && (
          <p className="text-xs text-muted-foreground" data-testid="not-found-autoremove">
            Auto-remove from your target list is {autoRemove ? 'ON' : 'OFF'} for this slot.
            {autoRemove ? ' Restore also puts the target back in the list.' : ''}
          </p>
        )}
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="not-found-empty">
            No targets are being skipped on this slot.
          </p>
        ) : (
          <ul className="divide-y divide-border/60" data-testid="not-found-list">
            {rows.map((r) => {
              const key = rowKey(r);
              const label = typeOf(r) ? typeLabel(r.type) : null;
              return (
                <li key={key} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`not-found-row-${key}`}>
                  <div className="min-w-0">
                    {label ? (
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate font-mono text-sm">{r.target}</p>
                        <span
                          className="shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[11px] leading-none text-muted-foreground"
                          data-testid={`not-found-type-${key}`}
                        >
                          {label}
                        </span>
                      </div>
                    ) : (
                      <p className="truncate font-mono text-sm">{r.target}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {r.misses !== null ? `Not found ${r.misses} time${r.misses === 1 ? '' : 's'}` : 'Not found'}
                      {r.missDays.length ? ` (${r.missDays.join(', ')})` : ''}
                      {r.skippedSince ? ` - skipped since ${formatSkippedSince(r.skippedSince)}` : ''}
                      {r.removedFromList ? ' - removed from the target list' : ''}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="tap rounded-full"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(r)}
                    aria-label={label ? `Restore ${r.target} (${label})` : undefined}
                    data-testid={`not-found-restore-${key}`}
                  >
                    <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden /> Restore
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <Card className="aurora-card" data-testid="not-found-targets-card">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
          <SearchX className="h-4 w-4 text-accent" aria-hidden />
          Targets not found (skipped)
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 pt-0 text-sm sm:p-6 sm:pt-0">
        <p className="mb-3 text-xs text-muted-foreground">
          A target that search could not find on 3 different days is skipped for this slot.
        </p>
        {body}
      </CardContent>
    </Card>
  );
}
